import { useState, useEffect, useCallback, useRef } from 'react';
import type { ChangeEvent, DragEvent, RefObject } from 'react';
import { Type, Upload, Image, Palette, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useI18n } from '@/i18n';
import { useToast } from '@/hooks/use-toast';
import { getSettings, updateSettings } from '@/api/settings';
import { uploadSettingsImage, type SettingsImageField } from '@/api/upload';

interface SettingsFormData {
  name: string;
  logo: string;
  banner: string;
  primaryColor: string;
  welcomeMessage: string;
}

interface ImageUploaderConfig {
  field: SettingsImageField;
  label: string;
  value: string;
  inputRef: RefObject<HTMLInputElement>;
  zoneClassName: string;
  imageClassName: string;
  emptyText: string;
  sizeHint: string;
}

export default function SettingsPage() {
  const { t } = useI18n();
  const { toast } = useToast();
  const logoInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  // Data states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState<SettingsImageField | null>(null);
  const [dragOverImage, setDragOverImage] = useState<SettingsImageField | null>(null);

  const [formData, setFormData] = useState<SettingsFormData>({
    name: '',
    logo: '',
    banner: '',
    primaryColor: '#0ea5e9',
    welcomeMessage: '',
  });

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getSettings();
      setFormData({
        name: result.name || '',
        logo: result.logo || '',
        banner: result.banner || '',
        primaryColor: result.primaryColor || '#0ea5e9',
        welcomeMessage: result.welcomeMessage || '',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load settings';
      toast({
        title: t.common.error,
        description: message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast, t.common.error]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    if (uploadingImage) return;

    try {
      setSaving(true);
      await updateSettings(formData);
      toast({
        title: t.common.success,
        description: t.admin.settingsSaved,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save settings';
      toast({
        title: t.common.error,
        description: message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleColorChange = (color: string) => {
    setFormData({ ...formData, primaryColor: color });
  };

  const uploadImageFile = async (field: SettingsImageField, file: File) => {
    if (!file.type.startsWith('image/')) {
      toast({
        title: t.common.error,
        description: t.admin.imageOnly,
        variant: 'destructive',
      });
      return;
    }

    try {
      setUploadingImage(field);
      const imageUrl = await uploadSettingsImage(file, field);
      setFormData((prev) => ({ ...prev, [field]: imageUrl }));
      toast({
        title: t.common.success,
        description: t.admin.imageUploadSuccess,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : t.admin.imageUploadFailed;
      toast({
        title: t.common.error,
        description: message,
        variant: 'destructive',
      });
    } finally {
      setUploadingImage(null);
    }
  };

  const handleImageInputChange = async (
    field: SettingsImageField,
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    if (file) {
      await uploadImageFile(field, file);
    }
    input.value = '';
  };

  const handleImageDragOver = (field: SettingsImageField, event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (uploadingImage) return;

    if (Array.from(event.dataTransfer.types).includes('Files')) {
      setDragOverImage(field);
    }
  };

  const handleImageDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOverImage(null);
  };

  const handleImageDrop = async (field: SettingsImageField, event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOverImage(null);

    const file = event.dataTransfer.files?.[0];
    if (file) {
      await uploadImageFile(field, file);
    }
  };

  const renderImageUploader = ({
    field,
    label,
    value,
    inputRef,
    zoneClassName,
    imageClassName,
    emptyText,
    sizeHint,
  }: ImageUploaderConfig) => {
    const isUploading = uploadingImage === field;
    const isDragOver = dragOverImage === field;

    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
        <div className={field === 'logo' ? 'grid gap-3 sm:grid-cols-[7rem_1fr]' : 'space-y-2'}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => {
              if (!uploadingImage) inputRef.current?.click();
            }}
            onKeyDown={(event) => {
              if ((event.key === 'Enter' || event.key === ' ') && !uploadingImage) {
                event.preventDefault();
                inputRef.current?.click();
              }
            }}
            onDragOver={(event) => handleImageDragOver(field, event)}
            onDragLeave={handleImageDragLeave}
            onDrop={(event) => {
              handleImageDrop(field, event);
            }}
            className={`relative flex items-center justify-center overflow-hidden rounded-xl border-2 border-dashed transition-colors ${zoneClassName} ${
              isDragOver
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-300 bg-gray-100 hover:border-primary-400'
            } ${uploadingImage ? 'cursor-wait' : 'cursor-pointer'}`}
          >
            {value ? (
              <img src={value} alt={label} className={`w-full h-full rounded-xl ${imageClassName}`} />
            ) : (
              <div className="px-3 text-center">
                <Upload className="w-8 h-8 text-gray-400 mx-auto" />
                <p className="text-sm text-gray-500 mt-2">{emptyText}</p>
                <p className="text-xs text-gray-400 mt-1">{t.admin.uploadImage}</p>
              </div>
            )}

            {isUploading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 text-primary-600">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="text-xs font-medium mt-2">{t.admin.uploadingImage}</span>
              </div>
            )}

            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="hidden"
              disabled={Boolean(uploadingImage)}
              onChange={(event) => {
                handleImageInputChange(field, event);
              }}
            />
          </div>

          <div className="space-y-1">
            <Input
              placeholder={t.admin.logoUrl}
              value={value}
              onChange={(event) => {
                const url = event.target.value;
                setFormData((prev) => ({ ...prev, [field]: url }));
              }}
            />
            <p className="text-xs text-gray-500">{sizeHint}</p>
            <p className="text-xs text-gray-400">{t.admin.uploadImageHint}</p>
          </div>
        </div>
      </div>
    );
  };

  const presetColors = [
    '#0ea5e9', // Sky blue
    '#8b5cf6', // Purple
    '#10b981', // Green
    '#f59e0b', // Amber
    '#ef4444', // Red
    '#ec4899', // Pink
    '#6366f1', // Indigo
    '#14b8a6', // Teal
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t.admin.settings}</h1>
          <p className="text-gray-500 mt-1">{t.admin.settingsDesc}</p>
        </div>
        <Button onClick={handleSave} disabled={saving || Boolean(uploadingImage)}>
          {saving || uploadingImage ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          {uploadingImage ? t.admin.uploadingImage : saving ? t.common.loading : t.admin.saveChanges}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Settings form */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Type className="w-5 h-5" />
                {t.admin.basicInfo}
              </CardTitle>
              <CardDescription>{t.admin.basicInfoDesc}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                label={t.admin.orgName}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t.admin.orgNamePlaceholder}
              />
              <Textarea
                label={t.admin.welcomeMessage}
                value={formData.welcomeMessage}
                onChange={(e) => setFormData({ ...formData, welcomeMessage: e.target.value })}
                placeholder={t.admin.welcomeMessagePlaceholder}
                rows={3}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="w-5 h-5" />
                {t.admin.branding}
              </CardTitle>
              <CardDescription>{t.admin.brandingDesc}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {renderImageUploader({
                field: 'logo',
                label: t.admin.logo,
                value: formData.logo,
                inputRef: logoInputRef,
                zoneClassName: 'w-28 h-28',
                imageClassName: 'object-contain',
                emptyText: t.admin.logo,
                sizeHint: t.admin.logoSize,
              })}

              {renderImageUploader({
                field: 'banner',
                label: t.admin.banner,
                value: formData.banner,
                inputRef: bannerInputRef,
                zoneClassName: 'w-full h-32',
                imageClassName: 'object-cover',
                emptyText: t.admin.addBanner,
                sizeHint: t.admin.bannerSize,
              })}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5" />
                {t.admin.themeColor}
              </CardTitle>
              <CardDescription>{t.admin.themeColorDesc}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3 mb-4">
                {presetColors.map((color) => (
                  <button
                    key={color}
                    onClick={() => handleColorChange(color)}
                    className={`w-10 h-10 rounded-lg transition-transform hover:scale-110 ${
                      formData.primaryColor === color
                        ? 'ring-2 ring-offset-2 ring-gray-400'
                        : ''
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-lg border border-gray-200"
                  style={{ backgroundColor: formData.primaryColor }}
                />
                <Input
                  value={formData.primaryColor}
                  onChange={(e) => handleColorChange(e.target.value)}
                  placeholder="#0ea5e9"
                  className="flex-1"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Preview */}
        <div>
          <Card className="sticky top-8">
            <CardHeader>
              <CardTitle>{t.admin.preview}</CardTitle>
              <CardDescription>{t.admin.previewDesc}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border rounded-xl overflow-hidden bg-gray-50">
                {/* Mock header */}
                <div
                  className="h-24 flex items-end px-4 pb-3"
                  style={{
                    background: formData.banner
                      ? `url(${formData.banner}) center/cover`
                      : `linear-gradient(135deg, ${formData.primaryColor}, ${formData.primaryColor}dd)`,
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center">
                      {formData.logo ? (
                        <img
                          src={formData.logo}
                          alt="Logo"
                          className="w-8 h-8 object-contain"
                        />
                      ) : (
                        <div
                          className="w-8 h-8 rounded-lg"
                          style={{ backgroundColor: formData.primaryColor }}
                        />
                      )}
                    </div>
                    <div className="text-white">
                      <h3 className="font-bold text-lg drop-shadow">{formData.name || t.admin.orgName}</h3>
                    </div>
                  </div>
                </div>

                {/* Welcome message */}
                <div className="p-4">
                  <p className="text-gray-600 text-sm mb-4">
                    {formData.welcomeMessage || t.admin.welcomeMessagePlaceholder}
                  </p>

                  {/* Mock app cards */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                      <div
                        className="w-10 h-10 rounded-lg"
                        style={{ backgroundColor: `${formData.primaryColor}20` }}
                      />
                      <div className="flex-1">
                        <div className="h-3 w-24 bg-gray-200 rounded mb-1.5" />
                        <div className="h-2 w-32 bg-gray-100 rounded" />
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                      <div
                        className="w-10 h-10 rounded-lg"
                        style={{ backgroundColor: `${formData.primaryColor}20` }}
                      />
                      <div className="flex-1">
                        <div className="h-3 w-20 bg-gray-200 rounded mb-1.5" />
                        <div className="h-2 w-28 bg-gray-100 rounded" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
